import { USE_API_MOCKS } from 'src/env';
import { PaginatedResponse } from '@dynamika-shared/types/general';
import {
  RemoveUploadValidationResponse,
  ReopenTaskValidationResponse,
  SupportQCTask,
  SupportReadingTask,
  SupportSubjectType,
  SupportTaskDefinition,
  SupportUpload,
  VisitDate,
} from '@dynamika-shared/types/subjects';
import { API_URL } from '@api/apiDictionary';
import { RESTClient, RESTClientMock } from '@api/client';
import { replacePathParams } from '@utils/replacePathWithParams';

const api = USE_API_MOCKS ? RESTClientMock : RESTClient;

type PaginatedArgs = {
  page?: number;
  size?: number;
  sort?: string;
};

type OptionalCommentRequestBody = {
  ticket?: string;
  comment?: string;
};

type ReopenTaskRequestBody = {
  ticket: string;
  comment: string;
};

export const fetchSubjectsByCT = async (
  ctId: string,
  args?: PaginatedArgs,
  search?: string
) => {
  const url = replacePathParams(API_URL.supportSubjectList, {
    ctId,
  });

  try {
    const response = await api.get<PaginatedResponse<SupportSubjectType>>(url, {
      params: {
        search,
        page: args?.page ? args.page - 1 : 0,
        size: args?.size ?? 10,
        sort: args?.sort,
      },
      headers: {
        'x-dynamika-clinical-trial-id': ctId,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching subjects by Clinical Trial:', error);
    throw error;
  }
};

export const fetchUploadsBySubject = async (
  ctId: string,
  patientId: string
) => {
  const path = replacePathParams(API_URL.supportSubject, {
    ctId,
    patientId,
  });

  const url = `${path}/uploads`;

  const response = await api.get<SupportUpload[]>(url, {
    headers: { 'x-dynamika-clinical-trial-id': ctId },
  });

  return response.data;
};

export const fetchTaskDefinitionsBySubject = async (
  ctId: string,
  patientId: string
) => {
  const path = replacePathParams(API_URL.supportSubject, {
    ctId,
    patientId,
  });

  const url = `${path}/task-definitions`;

  const response = await api.get<SupportTaskDefinition[]>(url, {
    headers: { 'x-dynamika-clinical-trial-id': ctId },
  });

  return response.data;
};

export const fetchReadingTasksBySubject = async (
  ctId: string,
  patientId: string
) => {
  const path = replacePathParams(API_URL.supportSubject, {
    ctId,
    patientId,
  });
  const url = `${path}/reading-tasks`;

  const response = await api.get<SupportReadingTask[]>(url, {
    headers: { 'x-dynamika-clinical-trial-id': ctId },
  });

  return response.data;
};

export const fetchQCTasksBySubject = async (
  ctId: string,
  patientId: string
) => {
  const path = replacePathParams(API_URL.supportSubject, {
    ctId,
    patientId,
  });
  const url = `${path}/is-tasks`;

  const response = await api.get<SupportQCTask[]>(url, {
    headers: { 'x-dynamika-clinical-trial-id': ctId },
  });

  return response.data;
};

export const fetchVisitDates = async (ctId: string, patientId: string) => {
  const path = replacePathParams(API_URL.supportSubject, {
    ctId,
    patientId,
  });
  const url = `${path}/visits/dates`;

  const response = await api.get<VisitDate>(url, {
    headers: { 'x-dynamika-clinical-trial-id': ctId },
  });

  return response.data;
};

export const validateRemoveUpload = async (ctId: string, uploadId: string) => {
  const path = replacePathParams(API_URL.supportRemoveUpload, {
    ctId,
    uploadId,
  });
  const url = `${path}/validate`;

  const response = await api.get<RemoveUploadValidationResponse>(url, {
    headers: { 'x-dynamika-clinical-trial-id': ctId },
  });

  return response.data;
};

export const removeUpload = async (
  ctId: string,
  uploadId: string,
  body: OptionalCommentRequestBody
) => {
  const url = replacePathParams(API_URL.supportRemoveUpload, {
    ctId,
    uploadId,
  });
  const response = await api.post<Response>(url, body, {
    headers: { 'x-dynamika-clinical-trial-id': ctId },
  });

  return response.data;
};

export const validateReopenVisit = async (
  ctId: string,
  taskId: string,
  signal?: AbortSignal
) => {
  const path = replacePathParams(API_URL.supportTaskReopen, {
    ctId,
    taskId,
  });

  const url = `${path}/validate`;

  const response = await api.get<ReopenTaskValidationResponse>(url, {
    headers: { 'x-dynamika-clinical-trial-id': ctId },
    signal,
  });

  return response.data;
};

export const reopenTask = async (
  ctId: string,
  taskId: string,
  body: ReopenTaskRequestBody
): Promise<Response> => {
  const url = replacePathParams(API_URL.supportTaskReopen, {
    ctId,
    taskId,
  });

  const response = await api.post<Response>(url, body, {
    headers: { 'x-dynamika-clinical-trial-id': ctId },
  });

  return response.data;
};
